import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { towRequestsApi, fromAxiosError } from '@/core/api';
import type { GetListTowRequestDTO } from '@/core/api';
import { EmptyState, StatusChip, useFormattedDate } from '@/shared';
import { relativeTime } from '@/core/utils';

/** Rows shown. Enough to be a work queue, short enough to scan. */
const ROW_LIMIT = 8;

/**
 * Statuses that mean "this job is yours and not finished".
 *
 * WINNER is won but not yet moving, TOARRIVE is en route, INPROCESS is the tow
 * itself. COMPLETED and CLOSE are deliberately absent — a finished job is
 * history, and history does not belong in a queue you are meant to act on.
 *
 * The list endpoint's `status` filter takes one value, so this is one request
 * per status rather than one request filtered down. Three small calls beats
 * fetching everything unfiltered and paging past a wall of completed jobs to
 * find the live ones.
 */
const ACTIVE_STATUSES = ['WINNER', 'TOARRIVE', 'INPROCESS'] as const;

function detailPath(row: GetListTowRequestDTO): string {
  return `/tow-requests/${row.requestTowId}?serviceType=${encodeURIComponent(row.serviceType ?? '')}`;
}

/** Oldest first — a job sitting untouched is the one that needs attention. */
function byAgeAscending(a: GetListTowRequestDTO, b: GetListTowRequestDTO): number {
  const at = a.requestDate ?? a.creationDate ?? '';
  const bt = b.requestDate ?? b.creationDate ?? '';
  return at < bt ? -1 : at > bt ? 1 : 0;
}

/**
 * The provider's live work queue.
 *
 * The KPI cards above say *how many* jobs are at each stage; this says *which*,
 * which is what someone actually acts on. Without it the dashboard could tell a
 * dispatcher they had four trucks to send and give them no way to find out
 * where.
 */
export default function ProviderActiveJobs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatLocalized = useFormattedDate();

  const [rows, setRows] = useState<GetListTowRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);

    Promise.all(
      ACTIVE_STATUSES.map((status) =>
        towRequestsApi
          .getTowRequestsList({ status, page: 0, pageSize: ROW_LIMIT })
          // One dead status must not blank the whole panel — the other two are
          // still worth showing.
          .then((r) => r.elements ?? [])
          .catch(() => [] as GetListTowRequestDTO[]),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const merged = results.flat().sort(byAgeAscending).slice(0, ROW_LIMIT);
        setRows(merged);
      })
      .catch((err) => {
        if (!cancelled) setErrorMessage(fromAxiosError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const ns = 'HOME.DASHBOARD.TOW-PROVIDER.activeJobs';

  return (
    <Paper sx={{ p: 3, height: '100%' }} aria-busy={loading}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'medium' }}>
          {t(`${ns}.title`)}
        </Typography>
        <MuiLink component={RouterLink} to="/tow-requests" variant="body2">
          {t(`${ns}.viewAll`)}
        </MuiLink>
      </Box>

      {loading ? (
        <Box>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Box>
      ) : errorMessage !== null ? (
        <EmptyState title={errorMessage} />
      ) : rows.length === 0 ? (
        <EmptyState title={t(`${ns}.empty`)} description={t(`${ns}.emptyHelp`)} />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('TOWING-REQUEST.claimNumber')}</TableCell>
                <TableCell>{t('TOWING-REQUEST.customer')}</TableCell>
                <TableCell>{t('TOWING-REQUEST.status')}</TableCell>
                <TableCell align="right">{t('TOWING-REQUEST.requestDate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.serviceType}-${row.requestTowId}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(detailPath(row))}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{row.claimNumber || '—'}</TableCell>
                  <TableCell>{row.nameCustomer || '—'}</TableCell>
                  <TableCell>
                    <StatusChip
                      status={row.status}
                      // Same namespace the tow-requests list uses, so a status
                      // reads identically here and there.
                      label={t(`STATUS-REPORT-SERVICES.${row.status}`, { defaultValue: row.status })}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={formatLocalized(row.requestDate ?? row.creationDate)}>
                      <span>{relativeTime(row.requestDate ?? row.creationDate) ?? '—'}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
